import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon, GripVerticalIcon, SearchXIcon } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PINNED_KEYS } from '../../utils/columns';
import { formatCurrency, fullName } from '../../utils/format';
import { Checkbox } from '../ui/Checkbox';
import { ColumnHeaderMenu } from './ColumnHeaderMenu';
import { ResizeHandle } from './ResizeHandle';

const SELECT_WIDTH = 48;
const LINE = '#e5e5e5'; // tw-neutral-200
const BRAND = '#98302a'; // tw-brand-600

export function RegistrationsTable(props) {
  const {
    rows, groups, collapsedGroups, onToggleGroup, columns, meta, widths, onResize, onResetWidth, onMoveColumn, ctx, sort, onSort,
    filterOptions, columnFilters, textFilters, onFilterChange, onTextFilterChange, selectedIds, onToggleRow, onToggleAll,
    allSelected, someSelected, density, activeId, onClearFilters,
  } = props;

  const tbodyRef = useRef(null);
  const resizingRef = useRef(false);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const widthOf = c => widths[c.key] ?? (density === 'compact' ? c.compactWidth ?? c.width : c.width);
  // Below 640px the pinned columns (436px with the checkbox) would cover a phone screen and leave
  // nothing to scroll, so nothing is pinned there.
  const pin = useMediaQuery('(min-width: 640px)');

  const { offsets, lastSticky } = useMemo(() => {
    const map = {};
    let left = SELECT_WIDTH;
    let last = null;
    if (!pin) return { offsets: map, lastSticky: last };
    for (const c of columns) {
      if (!PINNED_KEYS.includes(c.key)) break;
      map[c.key] = left;
      left += widths[c.key] ?? (density === 'compact' ? c.compactWidth ?? c.width : c.width);
      last = c.key;
    }
    return { offsets: map, lastSticky: last };
  }, [columns, density, widths, pin]);

  useEffect(() => {
    if (!activeId) return;
    tbodyRef.current?.querySelector(`tr[data-row-id="${CSS.escape(activeId)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const tableWidth = SELECT_WIDTH + columns.reduce((sum, c) => sum + widthOf(c), 0);
  const cellY = density === 'compact' ? 'tw-py-1.5' : 'tw-py-3';
  const visibleCount = groups ? groups.reduce((n, g) => n + g.rows.length, 0) : rows.length;

  // The pinned columns' right edge and the drop marker are inset shadows: with
  // border-separate, a border on a sticky cell would scroll away with the row.
  const stickyStyle = key => (offsets[key] !== undefined ? { position: 'sticky', left: offsets[key], zIndex: 2 } : undefined);
  const edgeShadow = key => (key === lastSticky ? `inset -1px 0 0 ${LINE}` : undefined);

  const focusSibling = (current, delta) => {
    const list = Array.from(tbodyRef.current?.querySelectorAll('tr[data-row]') ?? []);
    const next = list[list.indexOf(current) + delta];
    if (!next) return;
    next.focus();
    if (activeId && next.dataset.rowId) ctx.onOpen(next.dataset.rowId);
  };

  const renderRow = r => {
    const selected = selectedIds.has(r.id);
    const active = r.id === activeId;
    const rowBg = selected ? 'tw-bg-brand-50' : active ? 'tw-bg-neutral-50' : 'tw-bg-white';
    return (
      <tr
        key={r.id}
        data-row
        data-row-id={r.id}
        tabIndex={0}
        aria-label={`${fullName(r)} — press Enter to open details`}
        aria-selected={selected}
        onClick={e => {
          const target = e.target;
          if (!e.currentTarget.contains(target)) return;
          if (target.closest('button, a, input, select, textarea, label')) return;
          if (window.getSelection()?.toString()) return;
          ctx.onOpen(r.id);
        }}
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            ctx.onOpen(r.id);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            focusSibling(e.currentTarget, e.key === 'ArrowDown' ? 1 : -1);
          } else if (e.key === 'x') {
            e.preventDefault();
            onToggleRow(r.id);
          }
        }}
        className={`${rowBg} tw-cursor-pointer tw-outline-none tw-transition-colors tw-duration-100 hover:tw-bg-neutral-50 focus-visible:tw-bg-brand-50`}>
        <td
          className={`${pin ? 'tw-sticky tw-left-0 tw-z-[2]' : ''} tw-border-b tw-border-neutral-200 tw-bg-inherit tw-px-4 tw-align-middle ${cellY}`}
          style={active ? { boxShadow: `inset 3px 0 0 ${BRAND}` } : undefined}>
          <Checkbox checked={selected} onChange={() => onToggleRow(r.id)} aria-label={`Select ${fullName(r)}`} />
        </td>
        {columns.map(c => (
          <td
            key={c.key}
            style={{ ...stickyStyle(c.key), boxShadow: edgeShadow(c.key) }}
            className={`tw-overflow-hidden tw-border-b tw-border-neutral-200 tw-bg-inherit tw-px-4 tw-align-middle ${cellY}`}>
            {c.render(r, ctx)}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="scrollbar-thin tw-overflow-x-auto">
      <table className="tw-table-fixed tw-border-separate tw-border-spacing-0 tw-text-sm" style={{ width: tableWidth, minWidth: '100%' }}>
        <colgroup>
          <col style={{ width: SELECT_WIDTH }} />
          {columns.map(c => (
            <col key={c.key} style={{ width: widthOf(c) }} />
          ))}
        </colgroup>
        <thead>
          <tr className="tw-bg-neutral-50">
            <th scope="col" className={`${pin ? 'tw-sticky tw-left-0 tw-z-[3]' : ''} tw-border-b tw-border-neutral-200 tw-bg-neutral-50 tw-px-4 tw-py-2.5 tw-text-left`}>
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                onChange={onToggleAll}
                aria-label={allSelected ? 'Deselect all results' : 'Select all results'}
                disabled={!visibleCount}
              />
            </th>
            {columns.map(c => {
              const pinned = PINNED_KEYS.includes(c.key);
              const columnMeta = meta[c.key];
              return (
                <th
                  key={c.key}
                  scope="col"
                  draggable={!pinned}
                  aria-sort={sort?.key === c.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  onDragStart={e => {
                    if (resizingRef.current) {
                      e.preventDefault();
                      return;
                    }
                    setDragKey(c.key);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', c.key);
                  }}
                  onDragOver={e => {
                    if (!dragKey || pinned || dragKey === c.key) return;
                    e.preventDefault();
                    if (overKey !== c.key) setOverKey(c.key);
                  }}
                  onDragLeave={() => setOverKey(k => (k === c.key ? null : k))}
                  onDrop={e => {
                    e.preventDefault();
                    if (dragKey) onMoveColumn(dragKey, c.key);
                    setDragKey(null);
                    setOverKey(null);
                  }}
                  onDragEnd={() => {
                    setDragKey(null);
                    setOverKey(null);
                  }}
                  style={{ ...stickyStyle(c.key), zIndex: offsets[c.key] !== undefined ? 3 : undefined, boxShadow: overKey === c.key ? `inset 2px 0 0 ${BRAND}` : edgeShadow(c.key) }}
                  className={`tw-group/th tw-relative tw-border-b tw-border-neutral-200 tw-bg-neutral-50 tw-px-4 tw-py-2 tw-text-left tw-font-normal tw-transition-opacity tw-duration-150 ${dragKey === c.key ? 'tw-opacity-40' : ''}`}>
                  <div className="tw-flex tw-items-center">
                    {!pinned && (
                      <GripVerticalIcon
                        className="-tw-ml-3.5 tw-mr-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-cursor-grab tw-text-neutral-400 tw-opacity-0 tw-transition-opacity tw-duration-150 group-hover/th:tw-opacity-100"
                        aria-hidden
                      />
                    )}
                    <div className="tw-min-w-0 tw-flex-1">
                      <ColumnHeaderMenu
                        columnKey={c.key}
                        meta={columnMeta}
                        sort={sort}
                        onSort={onSort}
                        options={filterOptions[c.key] || []}
                        selected={columnFilters[c.key] ?? []}
                        onFilterChange={v => onFilterChange(c.key, v)}
                        textFilter={textFilters[c.key] ?? ''}
                        onTextFilterChange={v => onTextFilterChange(c.key, v)}
                      />
                    </div>
                  </div>
                  <ResizeHandle
                    label={columnMeta.label}
                    width={widthOf(c)}
                    resizingRef={resizingRef}
                    onResize={w => onResize(c.key, w)}
                    onReset={() => onResetWidth(c.key)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody ref={tbodyRef}>
          {groups
            ? groups.map(g => {
                const collapsed = collapsedGroups.has(g.key);
                return (
                  <React.Fragment key={g.key}>
                    <tr className="tw-bg-sand-50">
                      <td colSpan={columns.length + 1} className="tw-border-b tw-border-neutral-200 tw-p-0">
                        <div className="tw-sticky tw-left-0 tw-flex tw-w-max tw-items-center tw-gap-3 tw-px-4 tw-py-2.5">
                          <button
                            type="button"
                            onClick={() => onToggleGroup(g.key)}
                            aria-expanded={!collapsed}
                            className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-text-sm tw-font-semibold tw-text-neutral-900 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand-300">
                            <ChevronRightIcon className={`tw-h-4 tw-w-4 tw-text-neutral-500 tw-transition-transform tw-duration-150 ${collapsed ? '' : 'tw-rotate-90'}`} aria-hidden />
                            {g.label}
                            {g.continued && <span className="tw-text-xs tw-font-normal tw-text-neutral-400">(continued)</span>}
                          </button>
                          {g.sublabel && <span className="tw-text-xs tw-text-neutral-400">{g.sublabel}</span>}
                          <span className="tw-rounded-full tw-bg-white tw-px-2 tw-py-px tw-text-xs tw-font-medium tw-tabular-nums tw-text-neutral-500 tw-ring-1 tw-ring-inset tw-ring-neutral-200">
                            {g.total ?? g.rows.length}
                          </span>
                          <span className="tw-text-xs tw-text-neutral-500">
                            <span className={g.summary.amountDue ? 'tw-font-medium tw-text-amber-700' : ''}>{formatCurrency(g.summary.amountDue)} due</span>
                            {' · '}
                            {g.summary.registered} registered
                            {g.summary.missingDocuments > 0 && (
                              <>
                                {' · '}
                                <span className="tw-text-amber-700">{g.summary.missingDocuments} with missing documents</span>
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {!collapsed && g.rows.map(renderRow)}
                  </React.Fragment>
                );
              })
            : rows.map(renderRow)}
        </tbody>
      </table>
      {!visibleCount && (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-16 tw-text-center">
          <SearchXIcon className="tw-h-8 tw-w-8 tw-text-neutral-400" aria-hidden />
          <p className="tw-mt-3 tw-font-medium tw-text-neutral-900">No registrations match these filters</p>
          <p className="tw-mt-1 tw-text-sm tw-text-neutral-500">Try a different search or remove a filter.</p>
          <button
            type="button"
            onClick={onClearFilters}
            className="tw-mt-4 tw-h-9 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3.5 tw-text-sm tw-font-medium tw-text-neutral-900 tw-transition-colors hover:tw-bg-sand-50">
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}
